import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './ModalCobrarConta.module.css';

// Modal único de fechamento de conta de mesa. Usado tanto pelo cliente (Fechar
// Conta, via QR code) quanto pelo caixa (Receber Pagamento) e pelo garçom
// (Cobrar Mesa) — a única diferença entre eles é a prop `paymentOrigin`.
//
// Dividir conta (ver PaymentService.dividirConta/pagarParte) é recurso de
// garçom/caixa — o cliente via QR code nunca vê a opção de dividir.
function ModalCobrarConta({ idMesa, paymentOrigin, onClose, onSuccess }) {
  const [resumo, setResumo] = useState(null);
  const [divisao, setDivisao] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [processando, setProcessando] = useState(false);
  const [erro, setErro] = useState('');

  // Rascunho de uma divisão ainda não confirmada. tipo 'igual': só numPessoas.
  // tipo 'itens': pessoas (rótulos) + atribuicoes { [id_item_pedido]: índice da pessoa }.
  const [montandoDivisao, setMontandoDivisao] = useState(null);
  const [parteEmCobranca, setParteEmCobranca] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;
  const podeDividir = paymentOrigin !== 'CLIENTE';

  const formasDisponiveis = paymentOrigin === 'CLIENTE'
    ? [{ valor: 'pix', label: 'Pix' }, { valor: 'cartao', label: 'Cartão' }]
    : [
        { valor: 'pix', label: 'Pix' },
        { valor: 'cartao', label: 'Cartão' },
        { valor: 'dinheiro', label: 'Dinheiro' },
        { valor: 'debito', label: 'Débito' },
        { valor: 'credito', label: 'Crédito' }
      ];

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  const nomeItem = (item) => item.produto?.nome_item || item.nome_item_externo || 'Produto';
  const todosItensDaConta = () => (resumo?.pedidos || []).flatMap((p) => p.itens || []);

  useEffect(() => {
    const carregarTudo = async () => {
      try {
        const response = await fetch(`${API_URL}/pagamentos/conta/${idMesa}`);
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Não há conta aberta para esta mesa.');
        }
        setResumo(await response.json());
      } catch (error) {
        setErro(error.message);
      }

      if (podeDividir) {
        try {
          const response = await fetch(`${API_URL}/pagamentos/conta/${idMesa}/divisao`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          if (response.ok) setDivisao(await response.json());
        } catch (error) {
          // Não bloqueia a tela de pagamento único se isso falhar.
        }
      }

      setCarregando(false);
    };

    carregarTudo();
  }, [API_URL, idMesa]);

  const cobrar = async (formaPagamento) => {
    setProcessando(true);
    setErro('');

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (paymentOrigin !== 'CLIENTE' && token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/pagamentos/cobrar`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ idMesa, formaPagamento, paymentOrigin })
      });

      const resultado = await response.json();
      if (!response.ok) {
        throw new Error(resultado.error || 'Erro ao cobrar a conta.');
      }

      if (resultado.redirectUrl) {
        window.location.href = resultado.redirectUrl;
        return;
      }

      if (onSuccess) onSuccess(resultado);
      if (onClose) onClose();
    } catch (error) {
      setErro(error.message);
    } finally {
      setProcessando(false);
    }
  };

  const confirmarDivisao = async () => {
    setProcessando(true);
    setErro('');

    try {
      const body = montandoDivisao.tipo === 'igual'
        ? { tipo: 'igual', partes: montandoDivisao.numPessoas }
        : {
            tipo: 'itens',
            partes: montandoDivisao.pessoas.map((rotulo, idx) => ({
              rotulo,
              itens_ids: todosItensDaConta()
                .filter((item) => montandoDivisao.atribuicoes[item.id_item] === idx)
                .map((item) => item.id_item)
            }))
          };

      const response = await fetch(`${API_URL}/pagamentos/conta/${idMesa}/dividir`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body)
      });

      const resultado = await response.json();
      if (!response.ok) throw new Error(resultado.error || 'Erro ao dividir a conta.');

      setDivisao(resultado);
      setMontandoDivisao(null);
    } catch (error) {
      setErro(error.message);
    } finally {
      setProcessando(false);
    }
  };

  const cancelarDivisao = async () => {
    setProcessando(true);
    setErro('');

    try {
      const response = await fetch(`${API_URL}/pagamentos/conta/${idMesa}/divisao`, {
        method: 'DELETE',
        headers: authHeaders()
      });

      const resultado = await response.json();
      if (!response.ok) throw new Error(resultado.error || 'Erro ao cancelar a divisão.');

      setDivisao(null);
    } catch (error) {
      setErro(error.message);
    } finally {
      setProcessando(false);
    }
  };

  const pagarParte = async (idParte, formaPagamento) => {
    setProcessando(true);
    setErro('');

    try {
      const response = await fetch(`${API_URL}/pagamentos/divisao/${idParte}/pagar`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ formaPagamento, paymentOrigin })
      });

      const resultado = await response.json();
      if (!response.ok) throw new Error(resultado.error || 'Erro ao cobrar essa parte.');

      if (resultado.redirectUrl) {
        window.location.href = resultado.redirectUrl;
        return;
      }

      setParteEmCobranca(null);

      // A divisão só continua existindo (status 'aberta') enquanto sobrar
      // parte pendente — se sumiu, é porque a última parte fechou a conta.
      const respostaDivisao = await fetch(`${API_URL}/pagamentos/conta/${idMesa}/divisao`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const divisaoAtualizada = respostaDivisao.ok ? await respostaDivisao.json() : null;

      if (!divisaoAtualizada) {
        if (onSuccess) onSuccess(resultado);
        if (onClose) onClose();
        return;
      }

      setDivisao(divisaoAtualizada);
    } catch (error) {
      setErro(error.message);
    } finally {
      setProcessando(false);
    }
  };

  const itensParaAtribuir = montandoDivisao?.tipo === 'itens' ? todosItensDaConta() : [];
  const todosItensAtribuidos = montandoDivisao?.tipo !== 'itens'
    || itensParaAtribuir.every((item) => montandoDivisao.atribuicoes[item.id_item] !== undefined);

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2>Fechar Conta</h2>

        {carregando && <p>Carregando conta da mesa...</p>}

        {!carregando && erro && !resumo && <p className={styles.erro}>{erro}</p>}

        {resumo && !montandoDivisao && !divisao && (
          <>
            <p className={styles.total}>
              Total da conta: <strong>R$ {parseFloat(resumo.total).toFixed(2)}</strong>
            </p>

            <div className={styles.formas}>
              {formasDisponiveis.map((forma) => (
                <button
                  key={forma.valor}
                  className={styles.btnForma}
                  disabled={processando}
                  onClick={() => cobrar(forma.valor)}
                >
                  {forma.label}
                </button>
              ))}
            </div>

            {podeDividir && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button
                  className={styles.btnCancelar}
                  disabled={processando}
                  onClick={() => setMontandoDivisao({ tipo: 'igual', numPessoas: 2 })}
                >
                  Dividir igualmente
                </button>
                <button
                  className={styles.btnCancelar}
                  disabled={processando}
                  onClick={() => setMontandoDivisao({ tipo: 'itens', pessoas: ['Pessoa 1', 'Pessoa 2'], atribuicoes: {} })}
                >
                  Dividir por item
                </button>
              </div>
            )}

            {erro && <p className={styles.erro}>{erro}</p>}
          </>
        )}

        {/* Montando uma divisão nova (ainda não confirmada) */}
        {resumo && montandoDivisao && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {montandoDivisao.tipo === 'igual' ? (
              <>
                <p>Quantas pessoas vão dividir a conta?</p>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <button
                    className={styles.btnForma}
                    disabled={montandoDivisao.numPessoas <= 2}
                    onClick={() => setMontandoDivisao((p) => ({ ...p, numPessoas: p.numPessoas - 1 }))}
                  >
                    −
                  </button>
                  <strong style={{ minWidth: 20, textAlign: 'center' }}>{montandoDivisao.numPessoas}</strong>
                  <button
                    className={styles.btnForma}
                    disabled={montandoDivisao.numPessoas >= 10}
                    onClick={() => setMontandoDivisao((p) => ({ ...p, numPessoas: p.numPessoas + 1 }))}
                  >
                    +
                  </button>
                </div>
                <p>
                  Cada pessoa paga aproximadamente{' '}
                  <strong>R$ {(parseFloat(resumo.total) / montandoDivisao.numPessoas).toFixed(2)}</strong>
                </p>
              </>
            ) : (
              <>
                <p>Escolha de quem é cada item:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '35vh', overflowY: 'auto' }}>
                  {itensParaAtribuir.map((item) => (
                    <div key={item.id_item} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ flex: 1 }}>{nomeItem(item)}</span>
                      <span>R$ {parseFloat(item.total_item).toFixed(2)}</span>
                      <select
                        value={montandoDivisao.atribuicoes[item.id_item] ?? ''}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setMontandoDivisao((prev) => {
                            const atribuicoes = { ...prev.atribuicoes };
                            if (valor === '') delete atribuicoes[item.id_item];
                            else atribuicoes[item.id_item] = Number(valor);
                            return { ...prev, atribuicoes };
                          });
                        }}
                      >
                        <option value="">Sem dono</option>
                        {montandoDivisao.pessoas.map((rotulo, idx) => (
                          <option key={idx} value={idx}>{rotulo}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>

                <button
                  className={styles.btnForma}
                  onClick={() =>
                    setMontandoDivisao((prev) => ({
                      ...prev,
                      pessoas: [...prev.pessoas, `Pessoa ${prev.pessoas.length + 1}`]
                    }))
                  }
                >
                  + Pessoa
                </button>

                <div style={{ fontSize: 13, color: '#555' }}>
                  {montandoDivisao.pessoas.map((rotulo, idx) => {
                    const totalPessoa = itensParaAtribuir
                      .filter((item) => montandoDivisao.atribuicoes[item.id_item] === idx)
                      .reduce((soma, item) => soma + parseFloat(item.total_item), 0);
                    return (
                      <p key={idx} style={{ margin: '2px 0' }}>
                        {rotulo}: R$ {totalPessoa.toFixed(2)}
                      </p>
                    );
                  })}
                </div>
              </>
            )}

            {erro && <p className={styles.erro}>{erro}</p>}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className={styles.btnCancelar}
                disabled={processando}
                onClick={() => { setMontandoDivisao(null); setErro(''); }}
              >
                Voltar
              </button>
              <button
                className={styles.btnForma}
                disabled={processando || !todosItensAtribuidos}
                onClick={confirmarDivisao}
              >
                Confirmar divisão
              </button>
            </div>
          </div>
        )}

        {/* Divisão já confirmada — cobrar cada parte */}
        {resumo && !montandoDivisao && divisao && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {divisao.partes.map((parte) => (
              <div key={parte.id_parte} style={{ border: '1px solid #e2e2e2', borderRadius: 8, padding: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>{parte.rotulo}</strong>
                  <span>R$ {parseFloat(parte.valor).toFixed(2)}</span>
                </div>

                {parte.status === 'aprovado' ? (
                  <span style={{ color: '#2f8f5b', fontSize: 13 }}>✓ Pago</span>
                ) : parteEmCobranca === parte.id_parte ? (
                  <div className={styles.formas} style={{ marginTop: 8 }}>
                    {formasDisponiveis.map((forma) => (
                      <button
                        key={forma.valor}
                        className={styles.btnForma}
                        disabled={processando}
                        onClick={() => pagarParte(parte.id_parte, forma.valor)}
                      >
                        {forma.label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <button
                    className={styles.btnForma}
                    disabled={processando}
                    style={{ marginTop: 8 }}
                    onClick={() => setParteEmCobranca(parte.id_parte)}
                  >
                    Cobrar
                  </button>
                )}
              </div>
            ))}

            {erro && <p className={styles.erro}>{erro}</p>}

            {divisao.partes.every((p) => p.status !== 'aprovado') && (
              <button className={styles.btnCancelar} disabled={processando} onClick={cancelarDivisao}>
                Cancelar divisão
              </button>
            )}
          </div>
        )}

        <button className={styles.btnCancelar} onClick={onClose} disabled={processando}>
          Fechar
        </button>
      </div>
    </div>,
    document.body
  );
}

export default ModalCobrarConta;
